import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Hash, Link2, Plus, SlackIcon, Trash2 } from 'lucide-react';
import { apiClient } from '../../../lib/api';
import { AdminOrganization, SlackChannelMapping } from '../../../types/admin';
import { SlackChannelOption } from '../../../types/slack';
import { useToastContext } from '../../../contexts/ToastContext';
import { Button, ConfirmationModal, Spinner } from '../../ui';
import { CustomSelect } from '../../common/CustomSelect';

/**
 * Admin management of the Slack agent channel → organization mapping.
 *
 * A mapping is a cross-tenant data-access grant: everyone in the mapped Slack
 * channel can converse with the agent against that organization's knowledge
 * graph. Unmapped channels are ignored by the bot.
 *
 * Humans pick a channel and a client — Slack's internal ids (team id,
 * channel id) are resolved automatically from the connected Slack account.
 */
export const SlackAgentTab: React.FC = () => {
  const { showToast } = useToastContext();

  const [mappings, setMappings] = useState<SlackChannelMapping[]>([]);
  const [orgs, setOrgs] = useState<AdminOrganization[]>([]);
  const [channels, setChannels] = useState<SlackChannelOption[]>([]);
  const [slackTeamId, setSlackTeamId] = useState<string | null>(null);
  const [slackConnected, setSlackConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<SlackChannelMapping | null>(null);

  const [selectedChannelId, setSelectedChannelId] = useState('');
  const [workspaceId, setWorkspaceId] = useState('');

  const orgNameById = useMemo(
    () => new Map(orgs.map((o) => [o.id, o.name || o.domain || o.id])),
    [orgs]
  );

  const orgOptions = useMemo(
    () =>
      orgs
        .map((o) => ({
          value: o.id,
          label: o.name ? (o.domain ? `${o.name} (${o.domain})` : o.name) : o.id,
        }))
        .sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase())),
    [orgs]
  );

  const channelOptions = useMemo(
    () =>
      channels
        .map((c) => ({ value: c.id, label: `#${c.name}` }))
        .sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase())),
    [channels]
  );

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const [maps, organizations, status] = await Promise.all([
        apiClient.listSlackChannelMap(),
        apiClient.getAdminOrganizations(),
        apiClient.getSlackStatus(),
      ]);
      setMappings(maps);
      setOrgs(organizations);
      setSlackConnected(status.connected);
      setSlackTeamId(status.team_id ?? null);
      if (status.connected) {
        // Separate call so a Slack API hiccup degrades the picker, not the tab.
        try {
          setChannels(await apiClient.getSlackChannels());
        } catch (error) {
          console.error('Failed to list slack channels:', error);
          showToast('Could not load Slack channels — try reloading', 'error');
        }
      }
    } catch (error) {
      console.error('Failed to load slack agent mappings:', error);
      showToast('Failed to load channel mappings', 'error');
    } finally {
      setIsLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleAdd = async () => {
    const channel = channels.find((c) => c.id === selectedChannelId);
    if (!channel || !slackTeamId || !workspaceId) return;
    setIsSaving(true);
    try {
      const saved = await apiClient.upsertSlackChannelMap({
        slack_team_id: slackTeamId,
        channel_id: channel.id,
        workspace_id: workspaceId,
        channel_name: channel.name,
      });
      setMappings((prev) => [
        saved,
        ...prev.filter(
          (m) => !(m.slack_team_id === saved.slack_team_id && m.channel_id === saved.channel_id)
        ),
      ]);
      setSelectedChannelId('');
      showToast(`#${channel.name} mapped successfully`, 'success');
    } catch (error) {
      console.error('Failed to map channel:', error);
      showToast('Failed to map channel', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!pendingDelete) return;
    const target = pendingDelete;
    setPendingDelete(null);
    try {
      await apiClient.deleteSlackChannelMap(target.slack_team_id, target.channel_id);
      setMappings((prev) =>
        prev.filter(
          (m) => !(m.slack_team_id === target.slack_team_id && m.channel_id === target.channel_id)
        )
      );
      showToast('Mapping removed', 'success');
    } catch (error) {
      console.error('Failed to delete channel mapping:', error);
      showToast('Failed to remove mapping', 'error');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Spinner size="md" text="Loading channel mappings..." center />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
      {/* Add mapping */}
      <section
        id="slack-agent-add-mapping"
        className="bg-white border border-neutral-200 rounded-lg shadow-card p-5"
      >
        <div className="flex items-center gap-2 mb-1">
          <Link2 className="w-4 h-4 text-primary-600" aria-hidden />
          <h2 className="text-13 font-semibold text-neutral-900">Give a client channel access</h2>
        </div>
        <p className="text-xs text-neutral-500 mb-4">
          Pick a Slack channel and the client it belongs to. Everyone in that channel can then
          ask the bot about that client&apos;s data. Channels without a mapping are ignored.
        </p>
        {!slackConnected ? (
          <div className="flex items-center gap-3 bg-neutral-50 border border-neutral-200 rounded-md px-4 py-3">
            <SlackIcon className="w-4 h-4 text-neutral-400" aria-hidden />
            <p className="text-sm text-neutral-600">
              Connect your Slack account in <span className="font-medium">Settings</span> to pick
              channels.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
            <div>
              <label
                htmlFor="slack-agent-channel-select"
                className="block text-sm font-medium text-neutral-700 mb-1"
              >
                Slack channel
              </label>
              <CustomSelect
                aria-label="Slack channel"
                value={selectedChannelId}
                onChange={setSelectedChannelId}
                options={channelOptions}
                placeholder="Select channel..."
                searchable
              />
            </div>
            <div>
              <label
                htmlFor="slack-agent-org-select"
                className="block text-sm font-medium text-neutral-700 mb-1"
              >
                Client organization
              </label>
              <CustomSelect
                aria-label="Client organization"
                value={workspaceId}
                onChange={setWorkspaceId}
                options={orgOptions}
                placeholder="Select organization..."
                searchable
              />
            </div>
            <Button
              variant="primary"
              size="md"
              onClick={handleAdd}
              disabled={isSaving || !selectedChannelId || !workspaceId}
              aria-label="Give the selected channel access to the selected organization"
            >
              <Plus className="w-4 h-4 mr-1.5" aria-hidden />
              {isSaving ? 'Mapping...' : 'Map channel'}
            </Button>
          </div>
        )}
      </section>

      {/* Mappings table */}
      <section
        id="slack-agent-mappings"
        className="bg-white border border-neutral-200 rounded-lg shadow-card overflow-hidden"
      >
        <div className="px-5 py-4 border-b border-neutral-200">
          <h2 className="text-13 font-semibold text-neutral-900">Channels with access</h2>
        </div>
        {mappings.length === 0 ? (
          <div className="px-5 py-12 text-center">
            <Hash className="w-8 h-8 text-neutral-300 mx-auto mb-3" aria-hidden />
            <p className="text-sm text-neutral-500">
              No channels mapped yet — the bot stays silent everywhere until one is added.
            </p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-neutral-50 text-left text-xs text-neutral-500">
                <th className="px-5 py-2.5 font-medium">Channel</th>
                <th className="px-5 py-2.5 font-medium">Client organization</th>
                <th className="px-5 py-2.5 font-medium">Mapped</th>
                <th className="px-5 py-2.5" />
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {mappings.map((m) => (
                <tr
                  key={`${m.slack_team_id}:${m.channel_id}`}
                  className="hover:bg-neutral-50 transition"
                >
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-1.5 text-neutral-900">
                      <Hash className="w-3.5 h-3.5 text-neutral-400" aria-hidden />
                      <span className="font-medium">{m.channel_name || m.channel_id}</span>
                    </div>
                  </td>
                  <td className="px-5 py-3 text-neutral-700">
                    {orgNameById.get(m.workspace_id) || m.workspace_id}
                  </td>
                  <td className="px-5 py-3 text-neutral-500">
                    {new Date(m.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-5 py-3 text-right">
                    <button
                      onClick={() => setPendingDelete(m)}
                      aria-label={`Remove access for ${m.channel_name || m.channel_id}`}
                      className="p-1.5 rounded text-neutral-400 hover:text-error-600 hover:bg-error-50 transition focus:outline-none focus:ring-2 focus:ring-error-600 focus:ring-opacity-20"
                    >
                      <Trash2 className="w-4 h-4" aria-hidden />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <ConfirmationModal
        isOpen={pendingDelete !== null}
        title="Remove channel access?"
        message={`The bot will stop responding in ${
          pendingDelete?.channel_name ? `#${pendingDelete.channel_name}` : 'this channel'
        } and its members will lose access to the client's data.`}
        confirmText="Remove"
        variant="danger"
        onConfirm={handleDelete}
        onClose={() => setPendingDelete(null)}
      />
    </div>
  );
};

export default SlackAgentTab;
